import React, { useCallback, useEffect, useState } from 'react';
import {
  client,
  createPatientWound,
  deletePatientWound,
  getPatientWounds,
  updatePatientWound,
} from '../../services/api';
import type {
  TaskListResponse,
  WoundResponse,
} from '../../services/api/types.gen';
import WoundsCard from './WoundsCard';
import WoundFormDialog from './WoundFormDialog';
import WoundAssessmentsDrawer from './WoundAssessmentsDrawer';
import DeleteWoundDialog from './DeleteWoundDialog';
import { useStore } from '../../store';
import { toDateStr } from './woundUtils';

interface PatientWoundsProps {
  patientId: string;
  visitId?: string;
  visits?: TaskListResponse[];
  onDocumentCreated?: () => void;
}

const PatientWounds: React.FC<PatientWoundsProps> = ({
  patientId,
  visitId,
  visits = [],
  onDocumentCreated,
}) => {
  const canAdd = !!visitId;
  const showError = useStore(state => state.showError);
  const [wounds, setWounds] = useState<WoundResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form dialog
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingWound, setEditingWound] = useState<WoundResponse | null>(null);

  // Delete confirmation
  const [deletingWound, setDeletingWound] = useState<WoundResponse | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Assessments drawer
  const [assessmentsWound, setAssessmentsWound] =
    useState<WoundResponse | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchWounds = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await getPatientWounds({
        path: { patient_id: patientId },
        query: { include_discontinued: true },
        client,
      });
      setWounds((resp.data as WoundResponse[]) ?? []);
    } catch {
      setError('Failed to load wounds');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchWounds();
  }, [fetchWounds]);

  const handleSaveWound = async (data: {
    location: string;
    wound_type: string;
    onset_date: string | null;
    body_map_point?: { x: number; y: number } | null;
    discontinued_date?: string | null;
    discontinued_reason?: string | null;
    discontinued_comments?: string | null;
  }) => {
    try {
      if (editingWound) {
        const resp = await updatePatientWound({
          path: { patient_id: patientId, wound_id: editingWound.id },
          body: {
            ...data,
            ...(visitId && data.discontinued_date
              ? { discontinued_on_visit_id: visitId }
              : {}),
          },
          client,
        });
        const updated = resp.data as WoundResponse;
        setWounds(prev =>
          prev.map(w =>
            w.id === updated.id
              ? { ...updated, assessment_count: w.assessment_count }
              : w
          )
        );
        if (assessmentsWound?.id === updated.id)
          setAssessmentsWound(prev =>
            prev
              ? { ...updated, assessment_count: prev.assessment_count }
              : prev
          );
      } else {
        const resp = await createPatientWound({
          path: { patient_id: patientId },
          body: { ...data, added_on_visit_id: visitId! },
          client,
        });
        const created = resp.data as WoundResponse;
        setWounds(prev => [created, ...prev]);
      }
      setIsFormOpen(false);
      setEditingWound(null);
    } catch (err) {
      console.error('Failed to save wound:', err);
      showError(
        editingWound
          ? 'Failed to update wound. Please try again.'
          : 'Failed to create wound. Please try again.'
      );
    }
  };

  const handleDiscontinueWound = async (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => {
    try {
      const resp = await updatePatientWound({
        path: { patient_id: patientId, wound_id: woundId },
        body: {
          discontinued_date: toDateStr(date),
          discontinued_reason: reason || null,
          discontinued_comments: comments.trim() || null,
          ...(visitId ? { discontinued_on_visit_id: visitId } : {}),
        },
        client,
      });
      const updated = resp.data as WoundResponse;
      setWounds(prev => prev.map(w => (w.id === updated.id ? updated : w)));
    } catch (err) {
      console.error('Failed to discontinue wound:', err);
      showError('Failed to discontinue wound. Please try again.');
      throw err;
    }
  };

  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteWound = async () => {
    if (!deletingWound) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deletePatientWound({
        path: { patient_id: patientId, wound_id: deletingWound.id },
        client,
      });
      const deletedId = deletingWound.id;
      setWounds(prev => prev.filter(w => w.id !== deletedId));
      setDeletingWound(null);
    } catch (err) {
      console.error('Failed to delete wound:', err);
      setDeleteError('Failed to delete wound. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenAssessments = (wound: WoundResponse) => {
    setAssessmentsWound(wound);
    setIsDrawerOpen(true);
  };

  return (
    <>
      <WoundsCard
        patientId={patientId}
        wounds={wounds}
        isLoading={isLoading}
        error={error}
        canAdd={canAdd}
        visitId={visitId}
        onDocumentCreated={onDocumentCreated}
        onRetry={fetchWounds}
        onAddWound={() => {
          setEditingWound(null);
          setIsFormOpen(true);
        }}
        onEditWound={wound => {
          setEditingWound(wound);
          setIsFormOpen(true);
        }}
        onDeleteWound={setDeletingWound}
        onDiscontinueWound={handleDiscontinueWound}
        onOpenAssessments={handleOpenAssessments}
      />

      <WoundFormDialog
        open={isFormOpen}
        onOpenChange={open => {
          setIsFormOpen(open);
          if (!open) setEditingWound(null);
        }}
        editingWound={editingWound}
        canDiscontinue={canAdd}
        onSave={handleSaveWound}
      />

      <WoundAssessmentsDrawer
        wound={assessmentsWound}
        open={isDrawerOpen}
        readOnly={!canAdd}
        visitId={visitId}
        visits={visits}
        onOpenChange={open => {
          setIsDrawerOpen(open);
          if (!open) fetchWounds();
        }}
      />

      <DeleteWoundDialog
        wound={deletingWound}
        isDeleting={isDeleting}
        error={deleteError}
        onConfirm={handleDeleteWound}
        onClose={() => {
          setDeletingWound(null);
          setDeleteError(null);
        }}
      />
    </>
  );
};

export default PatientWounds;
